import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, STATUS_LABELS, STATUS_CHIP, formatDateTR } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { Download, Tag, FileText, ArrowLeft, Printer, Wand2, Save, LayoutDashboard, Settings, Barcode as BarcodeIcon, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "../lib/auth";
import { FabricPicker, FabricSelect } from "./OrderNewPage";

export default function OrderDetailPage() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [tab, setTab] = useState("overview");
  const [fabrics, setFabrics] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); api.get("/fabrics").then(({data}) => setFabrics(data)).catch(()=>{}); }, [id]);

  if (!order) return <div className="text-slate-500">Yükleniyor...</div>;

  const download = async (fmt, missingOnly) => {
    try {
      const res = await api.get(`/exports/labels`, {
        params: { order_id: id, fmt, missing_only: missingOnly },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiketler_${order.order_number}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} dosyası indirildi`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generate = async () => {
    setConfirmOpen(false);
    setGenerating(true);
    try {
      const { data } = await api.post(`/orders/${id}/generate-labels`);
      toast.success(`${data.created} adet barkod etiketi oluşturuldu`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const totalToGenerate = order ? order.products.reduce((s, p) => s + p.quantity * p.pieces_per_product, 0) : 0;

  const canManage = hasRole(user, "production_manager");

  return (
    <div>
      <Link to="/siparisler" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-700 mb-3" data-testid="back-orders">
        <ArrowLeft size={16} /> Siparişlere Dön
      </Link>
      <PageHeader
        title={order.order_number}
        subtitle={`${order.customer_name}${order.reference ? " — Ref: " + order.reference : ""}`}
        actions={
          canManage && (
            <div className="flex gap-2 flex-wrap">
              {!order.barcodes_generated && (
                <button onClick={() => setConfirmOpen(true)} disabled={generating || totalToGenerate === 0} data-testid="generate-labels-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold uppercase tracking-wide">
                  <Wand2 size={18}/> {generating ? "Oluşturuluyor..." : "Barkodları Oluştur"}
                </button>
              )}
              {order.barcodes_generated && (
                <>
                  <button onClick={async () => {
                    try {
                      const res = await api.get(`/exports/labels-pdf`, { params: { order_id: id }, responseType: "blob" });
                      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                      const w = window.open(url, "_blank");
                      w?.addEventListener?.("load", () => setTimeout(() => w.print?.(), 500));
                      toast.success("PDF hazırlandı");
                    } catch (e) { toast.error(formatApiError(e)); }
                  }} data-testid="pdf-order-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm">
                    <Tag size={16} /> PDF Yazdır
                  </button>
                  <Link to={`/etiketler?order_id=${id}`} target="_blank">
                    <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-800 hover:bg-slate-100 font-bold text-sm" data-testid="preview-labels-btn">
                      <Tag size={16} /> Etiket Önizle
                    </button>
                  </Link>
                  <button onClick={() => download("csv", false)} data-testid="export-csv-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white hover:bg-blue-800 font-bold text-sm">
                    <Download size={16} /> CSV
                  </button>
                  <button onClick={() => download("xml", false)} data-testid="export-xml-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white hover:bg-blue-800 font-bold text-sm">
                    <FileText size={16} /> XML
                  </button>
                </>
              )}
            </div>
          )
        }
      />

      {/* Sipariş Notu — göz önünde tam-genişlik uyarı bandı */}
      {order.notes && (
        <div
          data-testid="order-notes-banner"
          className="mb-4 p-4 bg-amber-100 border-l-8 border-amber-500 text-amber-950 flex items-start gap-3 shadow-sm"
        >
          <AlertTriangle size={26} className="shrink-0 mt-0.5 text-amber-600"/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-black mb-0.5 text-amber-800">Sipariş Notu</div>
            <div className="font-bold text-base sm:text-lg leading-snug whitespace-pre-wrap break-words">{order.notes}</div>
          </div>
        </div>
      )}

      {/* Info card */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6 card-industrial p-6">
        <InfoCell label="Müşteri" value={order.customer_name} />
        {order.reference && <InfoCell label="Referans" value={order.reference} mono />}
        <InfoCell label="Sipariş Tarihi" value={formatDateTR(order.order_date)} mono />
        <InfoCell label="Sevkiyat Tarihi" value={formatDateTR(order.requested_shipment_date)} mono />
        {order.destination && <InfoCell label="Varış" value={order.destination} />}
        {order.barcodes_generated ? (
          <>
            <InfoCell label="Toplam Parça" value={order.total_pieces} mono huge />
            <InfoCell label="Hazır" value={`${order.ready_pieces} (${order.progress_pct}%)`} mono />
          </>
        ) : (
          <InfoCell label="Barkod Durumu" value="Henüz oluşturulmadı" />
        )}
        {/* Notlar üstte büyük sarı bantta gösteriliyor — burada tekrarlamayalım */}
      </div>

      {order.barcodes_generated && (
        <div className="mb-6">
          <div className="flex justify-between text-xs uppercase font-bold text-slate-500 mb-1">
            <span>Üretim İlerlemesi</span>
            <span>{order.progress_pct}%</span>
          </div>
          <div className="h-3 bg-slate-200 border border-slate-300">
            <div className="h-full bg-emerald-500" style={{ width: `${order.progress_pct}%` }} />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b-2 border-slate-300 mb-6">
        <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} icon={LayoutDashboard} testid="tab-overview">Genel Bakış</TabBtn>
        {canManage && (
          <TabBtn active={tab === "details"} onClick={() => setTab("details")} icon={Settings} testid="tab-details">Detaylar (Parça Sayısı)</TabBtn>
        )}
        {order.barcodes_generated && (
          <TabBtn active={tab === "pieces"} onClick={() => setTab("pieces")} icon={BarcodeIcon} testid="tab-pieces">Fiziksel Parçalar ({order.pieces.length})</TabBtn>
        )}
      </div>

      {tab === "overview" && <OverviewTab order={order} />}
      {tab === "details" && <DetailsTab order={order} fabrics={fabrics} onSaved={load} />}
      {tab === "pieces" && <PiecesTab order={order} />}

      {/* Confirmation modal for generate-labels */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setConfirmOpen(false)} data-testid="confirm-generate-modal">
          <div className="bg-white border-2 border-slate-900 w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-600 text-white grid place-items-center shrink-0"><Wand2 size={22}/></div>
              <div>
                <h3 className="font-display font-black text-xl text-slate-900">Barkodları Oluştur</h3>
                <p className="text-sm text-slate-600 mt-1">Aşağıdaki ürünler için toplam <b className="text-blue-700 font-mono">{totalToGenerate}</b> adet barkod oluşturulacak. Devam etmek ister misiniz?</p>
              </div>
              <button onClick={() => setConfirmOpen(false)} className="ml-auto text-slate-400 hover:text-slate-900"><X size={20}/></button>
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-300 mb-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr className="text-xs uppercase tracking-widest text-slate-600 border-b border-slate-300">
                    <th className="text-left px-3 py-2">Ürün</th>
                    <th className="text-right px-3 py-2">Adet</th>
                    <th className="text-right px-3 py-2">Parça</th>
                    <th className="text-right px-3 py-2">Barkod</th>
                  </tr>
                </thead>
                <tbody>
                  {order.products.map((p) => (
                    <tr key={p.id} className="border-b border-slate-200">
                      <td className="px-3 py-2"><b>{p.category}</b> {p.model} <span className="text-slate-500">/ {p.size}</span> {p.fabric && <span className="text-slate-500">· {p.fabric}</span>}</td>
                      <td className="px-3 py-2 text-right font-mono">{p.quantity}</td>
                      <td className="px-3 py-2 text-right font-mono text-blue-700">{p.pieces_per_product}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{p.quantity * p.pieces_per_product}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 border-2 border-slate-300 hover:border-slate-800 font-bold text-sm" data-testid="confirm-cancel">İptal</button>
              <button onClick={generate} disabled={generating} data-testid="confirm-generate" className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold uppercase text-sm">
                <CheckCircle2 size={16}/> {generating ? "Oluşturuluyor..." : "Onayla ve Oluştur"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-3 font-bold uppercase tracking-wide text-sm border-b-4 -mb-0.5 ${active ? "border-blue-700 text-blue-700 bg-white" : "border-transparent text-slate-500 hover:text-slate-800"}`}
    >
      <Icon size={16} /> {children}
    </button>
  );
}

function OverviewTab({ order }) {
  return (
    <section>
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-4 py-3">Kategori</th>
              <th className="text-left px-4 py-3">Ürün İsmi</th>
              <th className="text-left px-4 py-3">Ölçü</th>
              <th className="text-left px-4 py-3">Kumaş</th>
              <th className="text-right px-4 py-3">Adet</th>
              <th className="text-right px-4 py-3">Parça/Ürün</th>
              <th className="text-right px-4 py-3">Toplam Parça</th>
              <th className="text-left px-4 py-3">Durum</th>
            </tr>
          </thead>
          <tbody>
            {order.products.map((p) => (
              <tr key={p.id} className="border-b border-slate-200 last:border-0">
                <td className="px-4 py-3"><span className="chip chip-siparis">{p.category}</span></td>
                <td className="px-4 py-3 font-bold">{p.model}</td>
                <td className="px-4 py-3 font-mono">{p.size}</td>
                <td className="px-4 py-3 font-mono">{p.fabric || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{p.quantity}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-blue-700 font-bold">{p.pieces_per_product}</td>
                <td className="px-4 py-3 text-right font-display font-black tabular-nums text-lg">{p.total_pieces}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {Object.entries(p.status_counts).filter(([, v]) => v > 0).map(([s, v]) => (
                      <span key={s} className={STATUS_CHIP[s] + " text-[10px]"}>{v} × {STATUS_LABELS[s]}</span>
                    ))}
                    {p.generated_pieces === 0 && <span className="text-xs text-slate-500 italic">Barkod bekleniyor</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DetailsTab({ order, fabrics, onSaved }) {
  const [rows, setRows] = useState(order.products.map((p) => ({
    id: p.id,
    pieces_per_product: p.pieces_per_product,
    fabric: p.fabric,
    notes: p.notes,
    dirty: false,
    locked: p.generated_pieces > 0 && Object.entries(p.status_counts).some(([s, v]) => v > 0 && s !== "URETIM_BEKLIYOR"),
  })));

  const update = (id, patch) => setRows(rows.map(r => r.id === id ? { ...r, ...patch, dirty: true } : r));

  const saveAll = async () => {
    const dirty = rows.filter(r => r.dirty);
    if (dirty.length === 0) return toast.info("Değişiklik yok");
    try {
      for (const r of dirty) {
        await api.patch(`/orders/${order.id}/products/${r.id}`, {
          pieces_per_product: r.pieces_per_product,
          fabric: r.fabric,
          notes: r.notes,
        });
      }
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section>
      <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-700 text-sm text-blue-900">
        <b>Detaylar sekmesi:</b> Bu üründe kaç fiziksel parça olacağını belirleyin (örn. çift barkodlu baza = 2). Kaydettikten sonra "Barkodları Oluştur" ile tüm etiketler tek seferde üretilir. Üretime başlamış ürünler kilitlidir.
      </div>

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-4 py-3">Kategori</th>
              <th className="text-left px-4 py-3">Ürün İsmi</th>
              <th className="text-left px-4 py-3">Ölçü</th>
              <th className="text-right px-4 py-3">Adet</th>
              <th className="text-center px-4 py-3">Fiziksel Parça / Ürün</th>
              <th className="text-right px-4 py-3">Oluşacak Barkod</th>
              <th className="text-left px-4 py-3">Kumaş</th>
              <th className="text-left px-4 py-3">Not</th>
            </tr>
          </thead>
          <tbody>
            {order.products.map((p) => {
              const r = rows.find(x => x.id === p.id);
              return (
                <tr key={p.id} className="border-b border-slate-200 last:border-0">
                  <td className="px-4 py-3"><span className="chip chip-siparis">{p.category}</span></td>
                  <td className="px-4 py-3 font-bold">{p.model}</td>
                  <td className="px-4 py-3 font-mono">{p.size}</td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{p.quantity}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center items-center gap-1">
                      <button type="button" disabled={r.locked} onClick={() => update(r.id, { pieces_per_product: Math.max(1, r.pieces_per_product - 1) })} className="w-8 h-8 border-2 border-slate-800 font-bold text-lg disabled:opacity-30" data-testid={`ppp-dec-${p.id}`}>−</button>
                      <input
                        type="number" min="1" max="20"
                        value={r.pieces_per_product}
                        onChange={(e) => update(r.id, { pieces_per_product: parseInt(e.target.value || "1") })}
                        disabled={r.locked}
                        className="w-16 text-center py-1 border-2 border-slate-800 font-display font-black text-lg tabular-nums disabled:bg-slate-100 disabled:text-slate-500"
                        data-testid={`ppp-input-${p.id}`}
                      />
                      <button type="button" disabled={r.locked} onClick={() => update(r.id, { pieces_per_product: Math.min(20, r.pieces_per_product + 1) })} className="w-8 h-8 border-2 border-slate-800 font-bold text-lg disabled:opacity-30" data-testid={`ppp-inc-${p.id}`}>+</button>
                    </div>
                    {r.locked && <div className="text-[10px] text-red-700 text-center mt-1">Kilitli — üretime başlanmış</div>}
                  </td>
                  <td className="px-4 py-3 text-right font-display font-black tabular-nums text-blue-700 text-xl">{p.quantity * r.pieces_per_product}</td>
                  <td className="px-4 py-3">
                    <FabricSelect fabrics={fabrics} value={r.fabric} onChange={(v) => update(r.id, { fabric: v })} testid={`row-fabric-${p.id}`} />
                  </td>
                  <td className="px-4 py-3">
                    <input value={r.notes || ""} onChange={(e) => update(r.id, { notes: e.target.value })} className="w-full px-2 py-1 border border-slate-300" data-testid={`row-notes-${p.id}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end">
        <button onClick={saveAll} data-testid="save-details-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide">
          <Save size={18} /> Detayları Kaydet
        </button>
      </div>
    </section>
  );
}

function PiecesTab({ order }) {
  const { user } = useAuth();
  const canPrint = hasRole(user, ["admin", "production_manager"]);
  const [printing, setPrinting] = useState(null); // barcode currently printing

  const reprint = async (barcode) => {
    setPrinting(barcode);
    try {
      const res = await api.get("/exports/labels-pdf", {
        params: { barcode },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiket_${barcode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`Etiket indirildi: ${barcode}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPrinting(null);
    }
  };

  return (
    <section>
      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-4 py-2">Barkod</th>
              <th className="text-left px-4 py-2">Ürün</th>
              <th className="text-left px-4 py-2">Kumaş</th>
              <th className="text-left px-4 py-2">Ürün #</th>
              <th className="text-left px-4 py-2">Parça #</th>
              <th className="text-left px-4 py-2">Durum</th>
              {canPrint && <th className="text-right px-4 py-2">İşlem</th>}
            </tr>
          </thead>
          <tbody>
            {order.pieces.map((p, i) => (
              <tr key={p.id} className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"}`} data-testid={`piece-${p.barcode}`}>
                <td className="px-4 py-2 font-mono tabular-nums">{p.barcode}</td>
                <td className="px-4 py-2">{p.model} <span className="text-slate-500">/ {p.size}</span></td>
                <td className="px-4 py-2 font-mono">{p.fabric || "—"}</td>
                <td className="px-4 py-2 font-mono tabular-nums">{p.product_number}/{p.product_total}</td>
                <td className="px-4 py-2 font-mono tabular-nums">{p.piece_number}/{p.piece_total}</td>
                <td className="px-4 py-2"><span className={STATUS_CHIP[p.status]}>{STATUS_LABELS[p.status]}</span></td>
                {canPrint && (
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => reprint(p.barcode)}
                      disabled={printing === p.barcode}
                      data-testid={`reprint-${p.barcode}`}
                      title="Bu parçanın etiketini tek başına PDF olarak indir"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 hover:border-blue-700 hover:bg-blue-50 text-slate-700 hover:text-blue-800 text-xs font-bold uppercase disabled:opacity-40"
                    >
                      <Printer size={13}/>
                      {printing === p.barcode ? "..." : "Yeniden Bastır"}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InfoCell({ label, value, mono, huge, className }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{label}</div>
      <div className={`${mono ? "font-mono tabular-nums" : "font-semibold"} ${huge ? "font-display font-black text-3xl" : "text-base"} text-slate-900`}>{value}</div>
    </div>
  );
}
